import { HttpErrorResponse, HttpEvent, HttpEventType, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { GraphQLError } from 'graphql';
import { environment } from 'projects/semeris/src/environments/environment';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { RootState } from 'projects/semeris/src/store/root-state.model';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import {
  FAIL_NOTIFICATION_CONFIGURATION,
  NETWORK_DOWN_NOTIFICATION_KEY,
  SUCCESS_NOTIFICATION_CONFIGURATION,
  WARNING_NOTIFICATION_CONFIGURATION,
} from '../constants/notification-configurations';
import { DEFAULT_FORBIDDEN_NOTIFICATION, FORBIDDEN_NOTIFICATION_MAP, ForbiddenReason } from '../models/forbidden-reason.model';
import { GraphQLErrorCollection } from '../services/graphql.service';

const NOTIFICATION_TYPE_HEADER = 'x-semeris-notification-type';
const NOTIFICATION_TEXT_HEADER = 'x-semeris-notification-text';

/**
 * document-store's session-level refusal (`plugins/auth0-bearer.ts`): the bearer verified but
 * sdocs denies the account Workspace access. Matched on the message because it is the one
 * gateway 403 that is not a per-resource authorization result — it fails every gateway call,
 * and both boot-path callers (`ApplicationService.gatewayWorkspaces$` and
 * `WorkspaceRecordService.load`) swallow the error, so nothing else tells the user why every
 * Workspace screen is empty.
 */
const GATEWAY_SIGN_IN_DENIED = 'Your account is not permitted to sign in.';

/** No reload action: reloading cannot grant the access, and the rest of sdocs still works. */
const WORKSPACE_ACCESS_DENIED_NOTIFICATION = {
  message:
    'Your account is not permitted to use Workspace, so its screens (Agents, documents, datasets) are unavailable. ' +
    'The rest of sdocs is unaffected — contact your administrator.',
  id: 'workspace_access_denied',
  closable: true,
  config: { color: 'warning', duration: 0, position: 'top-center' },
} as const;

const NOTIFICATION_CONFIG_BY_TYPE = {
  SUCCESS: SUCCESS_NOTIFICATION_CONFIGURATION,
  WARNING: WARNING_NOTIFICATION_CONFIGURATION,
  ERROR: FAIL_NOTIFICATION_CONFIGURATION,
  INFO: { color: 'info', duration: 4_000 },
} as const;

@Injectable()
export class ApiRequestInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly store = inject<Store<RootState>>(Store);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return (next.handle(req) as Observable<HttpResponse<any>>).pipe(
      map((response: HttpResponse<any>) => {
        if (!!response && response.type === HttpEventType.Response) {
          this.dispatchHeaderNotification(response);
        }

        if (
          !!response &&
          response.type === HttpEventType.Response &&
          response.status === 200 &&
          response.body &&
          Array.isArray(response.body.errors)
        ) {
          const errors = response.body.errors as GraphQLError[];

          const forbidden = errors.find((e) => e?.extensions?.code === 'FORBIDDEN');

          if (forbidden) {
            this.dispatchForbiddenNotification(forbidden);
          }

          const redirectError = errors.find((e) => e?.extensions?.code === 'REDIRECT');

          if (redirectError) {
            window.location.href = redirectError.message;
          }
        }

        return response;
      }),
      timeout(environment.networkTimeout),
      catchError((error: HttpErrorResponse | TimeoutError | GraphQLErrorCollection) => {
        if (error) {
          let status: number;

          if (error instanceof GraphQLErrorCollection) {
            const customError = error.errors?.find((e) => e?.extensions?.code === 'FORBIDDEN');

            if (customError) {
              status = 403;
            }

            const redirectError = error.errors?.find((e) => e?.extensions?.code === 'REDIRECT');

            if (redirectError) {
              window.location.href = redirectError.message;
            }
          }

          if (this.isTimeoutError(error)) {
            status = 408;
          }

          if (!status) {
            status = (error as HttpErrorResponse).status;
          }

          switch (status) {
            case 0:
              // status 0 = the browser blocked/failed the request before a response
              // (CORS preflight rejection, TLS failure, offline). A cross-origin call
              // to another host (e.g. the workspace AI gateway) failing this way isn't
              // the user's network being down, so don't flip the whole app to offline
              // mode for it — only same-origin API/GraphQL failures count.
              if (this.isOwnApiRequest(req)) {
                this.store.dispatch(AppActions.EnterOfflineMode());
              }
              break;

            case 401:
              // Scoped like status 0 and 403: only our own API can end this session. An unlisted
              // gateway segment gets no bearer and 401s — logging out of sdocs for that would
              // end a perfectly valid session.
              if (this.isOwnApiRequest(req)) {
                localStorage.removeItem('semeris-user-email');
                localStorage.removeItem('semeris-user-display-name');
                this.authService
                  .logout({
                    logoutParams: {
                      returnTo: `${window.origin}/auth/logout`,
                    },
                  })
                  .subscribe();
              }
              break;

            case 403:
              // Same restriction as status 0 above: a 403 from the workspace gateway is a
              // workspace-domain authorization result, surfaced in context by workspace-ui.
              // Announcing it app-wide as "you no longer have access — reload" is wrong advice,
              // and while impersonating it stacks a second toast on the bridge's own notice.
              if (this.isOwnApiRequest(req) || error instanceof GraphQLErrorCollection) {
                this.dispatchForbiddenNotification(
                  error instanceof GraphQLErrorCollection
                    ? error.errors?.find((e) => e?.extensions?.code === 'FORBIDDEN')
                    : (error as HttpErrorResponse).error,
                );
              } else if (this.isGatewaySignInDenied(error as HttpErrorResponse)) {
                this.store.dispatch(AppActions.PushNotification({ ...WORKSPACE_ACCESS_DENIED_NOTIFICATION }));
              }
              break;

            case 408:
              // Scoped like 0/401/403: one cross-origin call to the workspace gateway or the AI
              // service running out of time is not evidence that the user's network is down, and
              // "the network appears to be down — reload" is wrong, unactionable advice for it.
              // Those failures are surfaced in context by the screen that made the call.
              if (this.isOwnApiRequest(req)) {
                this.store.dispatch(
                  AppActions.PushNotification({
                    message: 'The network appears to be down. Please try again shortly.',
                    action: 'Reload application',
                    id: 'reload_application',
                    closable: true,
                    config: {
                      color: 'warning',
                      duration: 0,
                      position: 'top-center',
                      key: NETWORK_DOWN_NOTIFICATION_KEY,
                    },
                  }),
                );
              }
              break;
          }
        }

        return throwError(error);
      }),
    ) as Observable<HttpEvent<any>>;
  }

  private dispatchForbiddenNotification(errorSource?: GraphQLError | { reason?: string; detail?: string }): void {
    const ext = (errorSource as any)?.extensions;
    const reason = ext?.reason ?? ext?.exception?.response?.reason ?? ext?.response?.reason ?? (errorSource as any)?.reason;
    const config =
      reason && FORBIDDEN_NOTIFICATION_MAP[reason as ForbiddenReason]
        ? FORBIDDEN_NOTIFICATION_MAP[reason as ForbiddenReason]
        : DEFAULT_FORBIDDEN_NOTIFICATION;

    this.store.dispatch(
      AppActions.PushNotification({
        message: config.message,
        action: config.action,
        id: config.id,
        closable: config.closable,
        // Forbidden notifications had no Material duration (persisted); several carry a
        // 'Reload application' action — keep them persistent so the action isn't lost.
        config: { color: config.color ?? 'error', duration: 0, position: 'top-center', key: config.key },
      }),
    );
  }

  /** See `GATEWAY_SIGN_IN_DENIED` — the one gateway 403 that has to be announced app-wide. */
  private isGatewaySignInDenied(error: HttpErrorResponse): boolean {
    return (error?.error as { message?: string } | null)?.message === GATEWAY_SIGN_IN_DENIED;
  }

  private isTimeoutError(error: unknown): error is TimeoutError {
    return error instanceof TimeoutError;
  }

  /** True when the request targets our own backend (REST or GraphQL) rather than a
   *  third-party host — used to scope offline-mode detection, logout and the forbidden
   *  notification to our own API.
   *
   *  Each prefix is checked for emptiness first: `runtime-environment` falls back to `''` when
   *  `assets/environment.js` never loaded (a failed asset fetch, or jsdom in tests), and
   *  `startsWith('')` is true of every URL — which would claim every gateway call as our own and
   *  undo all three scopings. */
  private isOwnApiRequest(req: HttpRequest<unknown>): boolean {
    return (
      (!!environment.apiUrl && req.url.startsWith(environment.apiUrl)) ||
      (!!environment.graphqlUrl && req.url.startsWith(environment.graphqlUrl))
    );
  }

  private dispatchHeaderNotification(response: HttpResponse<any>): void {
    const type = response.headers?.get(NOTIFICATION_TYPE_HEADER) as keyof typeof NOTIFICATION_CONFIG_BY_TYPE | null;
    const rawText = response.headers?.get(NOTIFICATION_TEXT_HEADER);

    if (!type || !rawText || !(type in NOTIFICATION_CONFIG_BY_TYPE)) {
      return;
    }

    this.store.dispatch(
      AppActions.PushNotification({
        message: decodeURIComponent(rawText),
        config: NOTIFICATION_CONFIG_BY_TYPE[type],
      }),
    );
  }
}
